import React, { useState } from "react";
import { Box, IconButton, Label } from "@primer/react";
import { CheckCircleIcon, CheckIcon, PencilIcon } from "@primer/octicons-react";
import moment from "moment";
import { chamSocKhachHangApi } from "../../api/chamSocKhachHangApi";
import { NotifyHelper } from "../../helpers/toast";
import { textareaSx } from "../../pages/crm-survey/KhoLead/khoLeadStyles";
import Button from "../ui/button";
import PhuHuynhCSKHDetailModal from "./detail-modal/PhuHuynhCSKHDetailModal";

interface ChamSocKhachHang {
  id: number;
  da_xu_ly: boolean;
  ten_khach_hang: string;
  dien_thoai: string;
  ma_hs?: string;
  ho_ten_hoc_sinh?: string;
  is_request: boolean;
  ngay_hen_tra_loi?: string;
  ngay_xu_ly?: string;
  noi_dung: string;
  nguoi_tao: string;
  ngay_tao: string;
  ghi_chu?: string;
  ket_qua_xu_ly?: string;
  ten_loai_lien_he?: string;
  ten_ket_qua_lien_he?: string;
}

interface PhuHuynhCSKHItemProps {
  ts_chamsockhachhang: ChamSocKhachHang;
  onChanged: () => void;
  // Kho Lead dùng lại card: tự mở modal sửa riêng
  onEdit?: () => void;
  // Bản ghi từ Lead lưu kết quả liên hệ ở ket_qua_xu_ly: xác nhận xử lý chỉ đánh dấu, không nhập kết quả
  keepResult?: boolean;
}

const formatDate = (dateString?: string, includeTime = false) => {
  // ngay_tao từ ts_chamsockhachhang_select có dạng dd/MM/yyyy HH:mm:ss, cần khai báo format
  const m = dateString ? moment(dateString, ["DD/MM/YYYY HH:mm:ss", moment.ISO_8601]) : null;
  if (!m || !m.isValid()) return "";
  return includeTime ? m.format("DD/MM/YYYY HH:mm") : m.format("DD/MM/YYYY");
};


const PhuHuynhCSKHItem: React.FC<PhuHuynhCSKHItemProps> = ({ ts_chamsockhachhang, onChanged, onEdit, keepResult }) => {
  const [isShowEditModal, setShowEditModal] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [ketQua, setKetQua] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { da_xu_ly, is_request, ma_hs, ho_ten_hoc_sinh, noi_dung, nguoi_tao, ngay_tao, ngay_hen_tra_loi, ngay_xu_ly, ket_qua_xu_ly, ghi_chu, ten_loai_lien_he, ten_ket_qua_lien_he } = ts_chamsockhachhang;
  const isOverdue = !da_xu_ly && !!ngay_hen_tra_loi && moment(ngay_hen_tra_loi).isBefore(moment(), "day");

  const handleQuickProcess = async () => {
    if (!keepResult && !ketQua.trim()) {
      NotifyHelper.Warning("Vui lòng nhập kết quả xử lý");
      return;
    }
    setIsSaving(true);
    try {
      const detail = await chamSocKhachHangApi.detail(ts_chamsockhachhang.id);
      if (!detail.is_success || !detail.data) {
        NotifyHelper.Error(detail.message || "Không tải được thông tin yêu cầu");
        return;
      }
      const res = await chamSocKhachHangApi.update({
        ...detail.data,
        da_xu_ly: true,
        ngay_xu_ly: new Date().toISOString(),
        ket_qua_xu_ly: keepResult ? detail.data.ket_qua_xu_ly : ketQua.trim(),
      });
      if (res.is_success) {
        NotifyHelper.Success("Đã cập nhật trạng thái xử lý");
        setIsProcessing(false);
        setKetQua("");
        onChanged();
      } else {
        NotifyHelper.Error(res.message || "Cập nhật không thành công");
      }
    } catch (e: any) {
      NotifyHelper.Error(e?.message ?? "Có lỗi xảy ra");
    } finally {
      setIsSaving(false);
    }
  };

  // Giống thẻ tương tác bên Kho Lead: đầu thẻ xám (loại liên hệ + kết quả, ngày bên phải), thân là nội dung + dòng phụ
  const title = ten_loai_lien_he || (is_request ? "Yêu cầu phụ huynh" : "Ghi chú tuyển sinh");
  const henXuLy = formatDate(ngay_hen_tra_loi);

  return (
    <Box sx={{
      border: "1px solid", borderColor: isOverdue ? "danger.muted" : "border.default",
      borderRadius: 2, overflow: "hidden", mb: 2, bg: "canvas.default",
    }}>
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
        px: 3, py: 2, bg: "canvas.subtle", borderBottom: "1px solid", borderColor: "border.muted",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", minWidth: 0 }}>
          <Box sx={{ fontWeight: "bold", fontSize: 1 }}>{title}</Box>
          {ten_ket_qua_lien_he && <Label variant="success">{ten_ket_qua_lien_he}</Label>}
          {!da_xu_ly && <Label variant="attention">Chưa xử lý</Label>}
          {isOverdue && <Label variant="danger">Quá hạn</Label>}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          {!da_xu_ly && !isProcessing && (
            <Button
              text="Xác nhận đã xử lý"
              size="small"
              variant="primary"
              leadingVisual={CheckIcon}
              onClick={() => setIsProcessing(true)}
              // Primary của theme là xanh dương, ép sang xanh lá; selector hover trùng của Primer để không bị ghi đè
              sx={{
                bg: "success.emphasis", borderColor: "success.emphasis",
                "&:hover:not([disabled]):not([data-inactive])": { bg: "#1a7f37", borderColor: "#1a7f37" },
              }}
            />
          )}
          <Box sx={{ fontSize: 0, color: "fg.muted", whiteSpace: "nowrap" }}>{formatDate(ngay_tao)}</Box>
          <IconButton
            icon={PencilIcon}
            aria-label="Chỉnh sửa"
            variant="invisible"
            size="small"
            onClick={() => (onEdit ? onEdit() : setShowEditModal(true))}
          />
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 2, fontSize: 1 }}>
        <Box sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{noi_dung}</Box>

        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 2, fontSize: 0, color: "fg.muted" }}>
          {nguoi_tao && <span>{nguoi_tao}</span>}
          {henXuLy && (
            <Box as="span" sx={{ color: isOverdue ? "danger.fg" : undefined }}>
              Lịch hẹn: <strong>{henXuLy}</strong>
            </Box>
          )}
          {ho_ten_hoc_sinh && <span>Học sinh: {ho_ten_hoc_sinh}{ma_hs ? ` (${ma_hs})` : ""}</span>}
          {ghi_chu && <span>Ghi chú: {ghi_chu}</span>}
        </Box>

        {/* Đã xử lý: kết quả xử lý */}
        {da_xu_ly && (ket_qua_xu_ly || ngay_xu_ly) && (
          <Box sx={{
            // Vừa nội dung, không kéo hết chiều ngang thẻ
            display: "flex", width: "fit-content", maxWidth: "100%", alignItems: "flex-start", gap: 2, mt: 2, px: 2, py: 1,
            borderRadius: 2, bg: "success.subtle", fontSize: 0,
          }}>
            <Box sx={{ color: "success.fg", mt: "1px", display: "flex" }}><CheckCircleIcon size={14} /></Box>
            <Box sx={{ minWidth: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {ket_qua_xu_ly || "Đã xử lý"}
            </Box>
            {ngay_xu_ly && <Box sx={{ color: "fg.muted", whiteSpace: "nowrap" }}>{formatDate(ngay_xu_ly)}</Box>}
          </Box>
        )}


      {isProcessing && (
        <Box sx={{ display: "grid", gap: 2, mt: 2, pt: 2, borderTop: "1px dashed", borderColor: "border.muted" }}>
          {keepResult ? (
            <Box sx={{ fontSize: 1, color: "fg.muted" }}>Đánh dấu lần chăm sóc này đã xử lý?</Box>
          ) : (
          <Box as="textarea"
            autoFocus
            value={ketQua}
            onChange={(e: any) => setKetQua(e.target.value)}
            placeholder="Nhập kết quả xử lý…"
            sx={{ ...textareaSx, minHeight: "64px" }}
          />
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button text="Hủy" size="small" disabled={isSaving}
              onClick={() => { setIsProcessing(false); setKetQua(""); }}
            />
            <Button text="Hoàn tất" size="small" variant="primary" isLoading={isSaving} onClick={handleQuickProcess} />
          </Box>
        </Box>
      )}
      </Box>

      {isShowEditModal && (
        <PhuHuynhCSKHDetailModal
          id={ts_chamsockhachhang.id}
          onCancel={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            onChanged();
          }}
        />
      )}
    </Box>
  );
};

export default PhuHuynhCSKHItem;
